import React, { useState, useEffect } from 'react';
import { Heart, Bookmark, Share2, FolderPlus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface PieMenuItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  color: string;
  onClick: () => void;
  subMenu?: PieMenuItem[];
}

interface PieMenuProps {
  isOpen: boolean;
  position: { x: number; y: number };
  items: PieMenuItem[];
  onClose: () => void;
}

export const PieMenu: React.FC<PieMenuProps> = ({ isOpen, position, items, onClose }) => {
  const [activeSubMenu, setActiveSubMenu] = useState<PieMenuItem[] | null>(null);
  const radius = 50; // Distance from center (reduced from 80)
  const itemSize = 44; // Size of each menu item (reduced from 56)

  useEffect(() => {
    if (!isOpen) {
      setActiveSubMenu(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const angleStep = (2 * Math.PI) / items.length;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with fade */}
          <motion.div 
            className="fixed inset-0 z-[9998] bg-black/20 backdrop-blur-sm" 
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          {/* Pie Menu */}
          <div
            className="fixed z-[9999]"
            style={{
              left: position.x,
              top: position.y,
              transform: 'translate(-50%, -50%)'
            }}
          >
            {/* Center indicator with pulse */}
            <motion.div 
              className="absolute inset-0 w-4 h-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full transform -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2 shadow-lg"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            >
              <motion.div
                className="absolute inset-0 bg-white/40 rounded-full"
                animate={{ scale: [1, 1.5, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.div>

            {/* Menu Items */}
            {items.map((item, index) => {
              const angle = angleStep * index - Math.PI / 2; // Start from top
              const x = Math.cos(angle) * radius;
              const y = Math.sin(angle) * radius;

              return (
                <motion.button
                  key={item.id}
                  className="absolute group"
                  style={{
                    left: `calc(50% + ${x}px)`,
                    top: `calc(50% + ${y}px)`,
                    transform: 'translate(-50%, -50%)',
                    width: itemSize,
                    height: itemSize
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.subMenu) {
                      setActiveSubMenu(item.subMenu);
                    } else {
                      item.onClick();
                      onClose();
                    }
                  }}
                  initial={{ scale: 0, x: 0, y: 0 }}
                  animate={{ scale: 1, x, y }}
                  exit={{ scale: 0, x: 0, y: 0 }}
                  transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 25,
                    delay: index * 0.05
                  }}
                  whileHover={{ scale: 1.2, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <motion.div 
                    className={`w-full h-full rounded-full ${item.color} shadow-2xl flex items-center justify-center text-white relative overflow-hidden`}
                    whileHover={{ boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)' }}
                  >
                    {/* Shimmer effect */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                      initial={{ x: '-100%' }}
                      whileHover={{ x: '100%' }}
                      transition={{ duration: 0.6 }}
                    />
                    <div className="relative z-10">
                      {item.icon}
                    </div>
                  </motion.div>
                  <motion.div 
                    className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-gradient-to-br from-gray-900 to-gray-800 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-xl border border-white/10"
                    initial={{ opacity: 0, y: -5 }}
                    whileHover={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {item.label}
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                  </motion.div>
                </motion.button>
              );
            })}

            {/* Sub Menu */}
            <AnimatePresence>
              {activeSubMenu && (
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  {activeSubMenu.map((subItem, index) => {
                    const subAngleStep = (2 * Math.PI) / activeSubMenu.length;
                    const subAngle = subAngleStep * index - Math.PI / 2;
                    const subX = Math.cos(subAngle) * (radius + 30);
                    const subY = Math.sin(subAngle) * (radius + 30);

                    return (
                      <motion.button
                        key={subItem.id}
                        className="absolute group"
                        style={{
                          left: `calc(${subX}px)`,
                          top: `calc(${subY}px)`,
                          transform: 'translate(-50%, -50%)',
                          width: itemSize - 8,
                          height: itemSize - 8
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          subItem.onClick();
                          onClose();
                        }}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{
                          type: 'spring',
                          stiffness: 400,
                          damping: 30,
                          delay: index * 0.05
                        }}
                        whileHover={{ scale: 1.15 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <motion.div 
                          className={`w-full h-full rounded-full ${subItem.color} shadow-xl flex items-center justify-center text-white relative overflow-hidden`}
                          whileHover={{ boxShadow: '0 15px 20px -5px rgba(0, 0, 0, 0.3)' }}
                        >
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                            initial={{ x: '-100%' }}
                            whileHover={{ x: '100%' }}
                            transition={{ duration: 0.5 }}
                          />
                          <div className="relative z-10">
                            {subItem.icon}
                          </div>
                        </motion.div>
                        <motion.div 
                          className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-gradient-to-br from-gray-900 to-gray-800 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-xl border border-white/10"
                          initial={{ opacity: 0, y: -5 }}
                          whileHover={{ opacity: 1, y: 0 }}
                        >
                          {subItem.label}
                          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                        </motion.div>
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </AnimatePresence>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

// Hook for managing pie menu state
export function usePieMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [pressTimer, setPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });

  const handleLongPressStart = (e: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    setStartPosition({ x: clientX, y: clientY });

    const timer = setTimeout(() => {
      setPosition({ x: clientX, y: clientY });
      setIsOpen(true);
    }, 500); // 500ms long press

    setPressTimer(timer);
  };

  const handleLongPressMove = (e: React.MouseEvent | React.TouchEvent) => {
    // Cancel long press if user moves more than 10px (they're dragging)
    if (pressTimer) {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      
      const deltaX = Math.abs(clientX - startPosition.x);
      const deltaY = Math.abs(clientY - startPosition.y);
      
      if (deltaX > 10 || deltaY > 10) {
        clearTimeout(pressTimer);
        setPressTimer(null);
      }
    }
  };

  const handleLongPressEnd = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }
  };

  const close = () => {
    setIsOpen(false);
  };

  return {
    isOpen,
    position,
    handleLongPressStart,
    handleLongPressMove,
    handleLongPressEnd,
    close
  };
}
